// documentPreview.js — pure helpers for the DL-049 "Full / Cover" document
// preview styles. Kept framework-free so the mode-resolution + labelling logic
// (the part where a wrong answer changes what a viewer sees) is unit-testable
// with no DOM.
//
// Preview styles:
//   - "icon"  → the app's existing icon+filename row (the current live render,
//               a.k.a. "Icon List"). This is the DEFAULT and is what renders
//               whenever the PDF_PREVIEW_STYLES feature flag is OFF.
//   - "full"  → first-page preview card (deck copy "First-page preview").
//   - "cover" → cover-style card (lavender treatment, deck copy "Cover style").

export const PREVIEW_STYLES = Object.freeze({
  ICON: "icon",
  FULL: "full",
  COVER: "cover",
});

const CARD_STYLES = new Set([PREVIEW_STYLES.FULL, PREVIEW_STYLES.COVER]);

/**
 * Resolve the effective preview style.
 *
 * Byte-identical-OFF guarantee: when `flagEnabled` is falsy this ALWAYS returns
 * "icon", regardless of any stored previewStyle prop — so a wheel authored with
 * the flag ON renders as today's icon list everywhere the flag is OFF.
 *
 * @param {boolean} flagEnabled  PDF_PREVIEW_STYLES_ENABLED
 * @param {string=} previewStyle stored block prop ("icon" | "full" | "cover")
 * @returns {"icon"|"full"|"cover"}
 */
export function resolvePreviewStyle(flagEnabled, previewStyle) {
  if (!flagEnabled) return PREVIEW_STYLES.ICON;
  if (
    previewStyle === PREVIEW_STYLES.FULL ||
    previewStyle === PREVIEW_STYLES.COVER
  ) {
    return previewStyle;
  }
  return PREVIEW_STYLES.ICON;
}

/** True when the resolved style is a preview CARD (full or cover), not the row. */
export function isPreviewCardStyle(style) {
  return CARD_STYLES.has(style);
}

// Friendly, honest type label derived from the real `doc.type` value. Per the
// documented design intent (pdf-viewer-elevated/index.md, check-documented-
// intent law) we deliberately DO NOT fabricate page count / file size — the
// document record stores neither — so the card's secondary line is this type
// label only.
const TYPE_LABELS = {
  pdf: "PDF document",
  doc: "Word document",
  docx: "Word document",
  xls: "Spreadsheet",
  xlsx: "Spreadsheet",
  csv: "CSV file",
  ppt: "Presentation",
  pptx: "Presentation",
  "g-docs": "Google Doc",
  "g-slides": "Google Slides",
  "g-forms": "Google Form",
  "g-sheets": "Google Sheet",
  "g-drive": "Google Drive file",
  onedrive: "OneDrive file",
};

/**
 * @param {string=} type doc.type (e.g. "pdf", "docx")
 * @returns {string} human label; "Document" when unknown/missing.
 */
export function getDocTypeLabel(type) {
  if (!type || typeof type !== "string") return "Document";
  return TYPE_LABELS[type.toLowerCase()] || "Document";
}

// Short uppercase chip label for the Cover-style card (deck: "PDF" / "DOC" /
// "XLS"). Collapses office variants (docx→DOC, xlsx→XLS, pptx→PPT) and falls
// back to "FILE" for types with no short form.
const TYPE_CHIPS = {
  pdf: "PDF",
  doc: "DOC",
  docx: "DOC",
  xls: "XLS",
  xlsx: "XLS",
  csv: "CSV",
  ppt: "PPT",
  pptx: "PPT",
};

/**
 * @param {string=} type doc.type
 * @returns {string} short chip label; "FILE" when unknown/missing.
 */
export function getDocTypeChip(type) {
  if (!type || typeof type !== "string") return "FILE";
  return TYPE_CHIPS[type.toLowerCase()] || "FILE";
}

// ---- DL-049 settings-control model (canonical deck: pdf-viewer-elevated/
// 02-view-selector-sidepane.html body + 03-icon-list.html + index.md:54) --------
//
// The Layout-tab control the deck specifies is a "Show preview" switch + a
// THREE-way "Preview style" segmented control [Full | Cover | Icon List] + a
// "Preview page" dropdown. "Icon List" == PREVIEW_STYLES.ICON, i.e. the app's
// EXISTING icon+filename row render, shown untouched (index.md documented intent:
// Icon List is "the version currently LIVE in the app", sourced not invented) —
// so no new render path is needed for it; selecting it routes through the same
// byte-identical icon render as the flag-OFF default.

// Ordered trio for the segmented control, in deck order (Full first, active by
// default when previews are turned on).
export const PREVIEW_STYLE_OPTIONS = Object.freeze([
  { value: PREVIEW_STYLES.FULL, label: "Full" },
  { value: PREVIEW_STYLES.COVER, label: "Cover" },
  { value: PREVIEW_STYLES.ICON, label: "Icon List" },
]);

// The single honest "Preview page" option. The real render engine
// (PdfFirstPagePreview) only rasterises page 1, and the document record stores
// no page count (check-documented-intent law + index.md: never fabricate
// pages/size), so the deck's "Preview page" field shows exactly "Page 1 · first
// page". Dimmed for Icon List (no page concept for a list).
export const PREVIEW_PAGE_OPTIONS = Object.freeze([
  { value: 1, label: "Page 1 · first page" },
]);

// Deck note copy, verbatim (bold phrase applied at render). U+00B7 middle dots
// match the canonical deck body exactly.
export const PREVIEW_NOTE_STYLES =
  "Three styles for every document type · pick per document " +
  "(PDF · DOC · XLS · PPT · more).";
export const PREVIEW_NOTE_ICON =
  "Icon List is the compact, current-app style · a file-type icon + " +
  "filename per document.";

/**
 * True when the "Show preview" switch is ON — i.e. an explicit preview style has
 * been chosen (full | cover | icon). `undefined` (never touched, the byte-
 * identical default) means the switch is OFF and the config is collapsed.
 * @param {string=} previewStyle
 * @returns {boolean}
 */
export function isPreviewShown(previewStyle) {
  return (
    previewStyle === PREVIEW_STYLES.FULL ||
    previewStyle === PREVIEW_STYLES.COVER ||
    previewStyle === PREVIEW_STYLES.ICON
  );
}

/**
 * Next previewStyle when the "Show preview" switch is toggled: ON -> undefined
 * (collapse to the byte-identical default), OFF -> "full" (deck default option).
 * @param {string=} previewStyle current value
 * @returns {string|undefined}
 */
export function nextToggledStyle(previewStyle) {
  return isPreviewShown(previewStyle) ? undefined : PREVIEW_STYLES.FULL;
}

/**
 * Contextual side-pane note under the segmented control (deck 02 vs 03).
 * @param {string=} previewStyle
 * @returns {string}
 */
export function previewStyleNote(previewStyle) {
  return previewStyle === PREVIEW_STYLES.ICON
    ? PREVIEW_NOTE_ICON
    : PREVIEW_NOTE_STYLES;
}

/**
 * The "Preview page" section is dimmed for Icon List (a list has no page).
 * @param {string=} previewStyle
 * @returns {boolean}
 */
export function isPreviewPageDimmed(previewStyle) {
  return previewStyle === PREVIEW_STYLES.ICON;
}
